import { signal } from '@preact/signals';

/** Theme engine. `<html data-theme>` is the source of truth (HANDOFF §6.1).
 *  mode: 'auto' follows sunset when a location is known, else the OS setting. */

export type ThemeMode = 'auto' | 'light' | 'dark';
export type Resolved = 'light' | 'dark';

const MODE_KEY = 'tend.theme.mode';
const RESOLVED_KEY = 'tend.theme.resolved'; // read by the pre-paint script in index.html
const LOC_KEY = 'tend.location';

export const themeMode = signal<ThemeMode>(load());
export const resolvedTheme = signal<Resolved>(
  (document.documentElement.dataset.theme as Resolved) || 'light'
);
/** Set briefly when an automatic sunset/sunrise switch happens — drives the announcement pill. */
export const sunsetAnnouncement = signal<null | { to: Resolved }>(null);

function load(): ThemeMode {
  const v = localStorage.getItem(MODE_KEY);
  return v === 'light' || v === 'dark' ? v : 'auto';
}

export function getLocation(): { lat: number; lng: number } | null {
  try {
    const raw = localStorage.getItem(LOC_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw);
    return typeof v?.lat === 'number' && typeof v?.lng === 'number' ? v : null;
  } catch {
    return null;
  }
}

export function setLocation(lat: number, lng: number) {
  localStorage.setItem(LOC_KEY, JSON.stringify({ lat, lng }));
  evaluate(false);
}

export function setThemeMode(mode: ThemeMode) {
  themeMode.value = mode;
  localStorage.setItem(MODE_KEY, mode);
  evaluate(false);
}

/** NOAA-simplified sunrise/sunset. Good to a couple of minutes — plenty for a theme switch. */
export function sunTimes(date: Date, lat: number, lng: number): { sunrise: Date; sunset: Date } {
  const rad = Math.PI / 180;
  const dayMs = 86400000;
  const J1970 = 2440588, J2000 = 2451545;
  const toJulian = (d: Date) => d.valueOf() / dayMs - 0.5 + J1970;
  const fromJulian = (j: number) => new Date((j + 0.5 - J1970) * dayMs);
  const lw = rad * -lng;
  const phi = rad * lat;
  const d = toJulian(date) - J2000;
  const n = Math.round(d - 0.0009 - lw / (2 * Math.PI));
  const ds = 0.0009 + lw / (2 * Math.PI) + n;
  const M = rad * (357.5291 + 0.98560028 * ds);
  const C = rad * (1.9148 * Math.sin(M) + 0.02 * Math.sin(2 * M) + 0.0003 * Math.sin(3 * M));
  const P = rad * 102.9372;
  const L = M + C + P + Math.PI;
  const dec = Math.asin(Math.sin(L) * Math.sin(rad * 23.4397));
  const Jnoon = J2000 + ds + 0.0053 * Math.sin(M) - 0.0069 * Math.sin(2 * L);
  const h0 = rad * -0.833; // standard refraction-corrected horizon
  const cosH = (Math.sin(h0) - Math.sin(phi) * Math.sin(dec)) / (Math.cos(phi) * Math.cos(dec));
  // Polar day/night fallback: pretend 6am/6pm so the app still functions.
  if (cosH < -1) return { sunrise: at(date, 0), sunset: at(date, 23, 59) };
  if (cosH > 1) return { sunrise: at(date, 11, 59), sunset: at(date, 12) };
  const H = Math.acos(cosH);
  const Jrise = Jnoon - H / (2 * Math.PI);
  const Jset = Jnoon + H / (2 * Math.PI);
  return { sunrise: fromJulian(Jrise), sunset: fromJulian(Jset) };
}

function at(d: Date, h: number, m = 0): Date {
  const r = new Date(d);
  r.setHours(h, m, 0, 0);
  return r;
}

function autoResolved(now = new Date()): Resolved {
  const loc = getLocation();
  if (loc) {
    const { sunrise, sunset } = sunTimes(now, loc.lat, loc.lng);
    return now >= sunrise && now < sunset ? 'light' : 'dark';
  }
  return matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

let announceTimer: ReturnType<typeof setTimeout> | undefined;

function apply(next: Resolved, announce: boolean) {
  if (resolvedTheme.value === next) return;
  resolvedTheme.value = next;
  document.documentElement.dataset.theme = next;
  localStorage.setItem(RESOLVED_KEY, next);
  const meta = document.querySelector('meta[name="theme-color"]:not([media])');
  if (meta) meta.setAttribute('content', next === 'dark' ? '#13120F' : '#FAF8F5');
  if (announce) {
    sunsetAnnouncement.value = { to: next };
    clearTimeout(announceTimer);
    announceTimer = setTimeout(() => (sunsetAnnouncement.value = null), 4400);
  }
}

/** Re-evaluate the resolved theme. `announce` shows the sunset pill on auto switches. */
export function evaluate(announce: boolean) {
  const mode = themeMode.value;
  if (mode === 'light' || mode === 'dark') return apply(mode, false);
  apply(autoResolved(), announce);
}

export function initTheme() {
  evaluate(false);
  // Minute tick catches the sunset/sunrise boundary while the app is open.
  setInterval(() => evaluate(true), 60_000);
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) evaluate(true);
  });
  matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => evaluate(true));
}
